import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';

import { combineLatest, Observable, Subscription, take } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import {
  FINNAIR,
  GtmEcommerceClickProduct,
  GtmEcommerceImpressionProduct,
  GtmEcommerceList,
  GtmEcommerceMeasurement,
  GtmEvent,
  GtmPurchaseFlow,
} from '@fcom/common/interfaces';
import { CmsImageData } from '@fcom/core-api/interfaces';
import { Category, FinnairBoundItem } from '@fcom/dapi/api/models';
import { Amount, SelectedServiceData } from '@fcom/dapi/interfaces';
import { unsubscribe } from '@fcom/core/utils';
import { GtmService } from '@fcom/common/gtm';
import { getGA4ProductsFromSmpProducts, resolveGtmCategory } from '@fcom/common/utils/gtm.utils';
import { MediaQueryService } from '@fcom/common/services';

import { ButtonState, ProductTag } from '../../interfaces';
import { LowQuotaSegment } from '../../utils';

export type PurchasesDonePerFragment = Record<string, boolean>;

export interface ProductCardNotification {
  messageKey?: string;
  titleKey?: string;
  icon?: SvgLibraryIcon;
  theme: NotificationTheme;
}

export interface SmpProduct {
  category: Category;
  title: string;
  description: string;
  image: CmsImageData;
  status$: Observable<ButtonState>;
  price$: Observable<Amount>;
  points$?: Observable<Amount>;
  count$: Observable<number>;
  purchasedCount$?: Observable<number>;
  maxNumberItems$?: Observable<number>;
  maxPurchasesDone$?: Observable<boolean>;
  notAvailable$?: Observable<boolean>;
  notEligibleWithIncompatibleSeat?: boolean;
  purchasesDoneForFragment$?: Observable<PurchasesDonePerFragment>;
  lowQuota$?: Observable<LowQuotaSegment | undefined>;
  noQuotaLeft: boolean;
  productCardNotification$: Observable<ProductCardNotification>;
  lowestPrice?: Amount;
  originalPrice?: Amount;
  lowestPriceAsPoints?: Amount;
  productTags?: ProductTag[];
  isBundle?: boolean;
}

@Component({
  selector: 'fin-product-list',
  styleUrls: ['product-list.component.scss'],
  templateUrl: 'product-list.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductListComponent implements OnInit, OnDestroy {
  private gtmService = inject(GtmService);
  private mediaQueryService = inject(MediaQueryService);

  readonly ButtonState = ButtonState;
  readonly ButtonTheme = ButtonTheme;
  readonly LoaderTheme = LoaderTheme;
  readonly Category = Category;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  private subscriptions: Subscription = new Subscription();
  isLaptopUp$: Observable<boolean>;

  @Input() hideZeroPrices = false;
  @Input() products$: Observable<SmpProduct[]>;
  @Input() showStackableLabels = false;
  @Input() tabletShowTwoPerRow = false;
  @Input() purchaseFlow: GtmPurchaseFlow;
  @Input() focusOnProductBtn$: Observable<Category>;
  @Input() productCardLocalizationsPrefix = '';
  @Input() bounds$: Observable<FinnairBoundItem[]>;
  @Input() listTitle?: string;
  @Input() useNewProductCardLayout = false;
  @Input() personalizationDiscountCode: string = undefined;

  @Output() selectProduct: EventEmitter<SelectedServiceData> = new EventEmitter<SelectedServiceData>();

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([this.products$, this.bounds$])
        .pipe(take(1))
        .subscribe(([products, bounds]) => {
          this.sendEcommerceImpressionsEvent(products);
          this.gtmService.ecommerceEventGA4(
            GtmEvent.VIEW_ITEM_LIST,
            getGA4ProductsFromSmpProducts(products, this.purchaseFlow, bounds, {}, this.personalizationDiscountCode)
          );
        })
    );
    this.isLaptopUp$ = this.mediaQueryService.isBreakpoint$('laptop_up');
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  onSelectProduct(category: Category, index: number): void {
    this.selectProduct.emit({ category, index: 0 });
    this.sendEcommerceClickEvent(category, index);

    this.subscriptions.add(
      combineLatest([this.products$, this.bounds$])
        .pipe(take(1))
        .subscribe(([products, bounds]) => {
          const matchedProduct = products.find((product) => product.category === category);
          this.gtmService.ecommerceEventGA4(
            GtmEvent.SELECT_ITEM,
            getGA4ProductsFromSmpProducts(
              [matchedProduct],
              this.purchaseFlow,
              bounds,
              {},
              this.personalizationDiscountCode
            )
          );
        })
    );
  }

  private sendEcommerceImpressionsEvent(products: SmpProduct[]): void {
    const gtmProducts: GtmEcommerceImpressionProduct[] = products.map((product, index) => {
      const name = product.category === Category.BAGGAGE ? 'BAG' : resolveGtmCategory(product.category)[0];
      return {
        name,
        id: name,
        brand: FINNAIR,
        category: resolveGtmCategory(product.category)[1],
        list: [GtmPurchaseFlow.BOOKING, GtmPurchaseFlow.CORPORATE].includes(this.purchaseFlow)
          ? GtmEcommerceList.BOOKING_SMP
          : GtmEcommerceList.MMB_VIEW_PNR,
        position: index + 1,
      };
    });

    this.gtmService.ecommerceEvent(
      GtmEvent.PRODUCT_IMPRESSIONS,
      GtmEcommerceMeasurement.IMPRESSIONS,
      gtmProducts,
      this.purchaseFlow
    );
  }

  private sendEcommerceClickEvent(category: Category, index: number): void {
    const gtmProductName = category === Category.BAGGAGE ? 'BAG' : resolveGtmCategory(category)[0];
    const gtmProduct: GtmEcommerceClickProduct = {
      name: gtmProductName,
      id: gtmProductName,
      brand: FINNAIR,
      category: resolveGtmCategory(category)[1],
      position: index + 1,
    };

    this.gtmService.enhancedEcommerceProductClickOrDetailView(
      GtmEvent.PRODUCT_CLICK,
      GtmEcommerceMeasurement.CLICK,
      gtmProduct,
      [GtmPurchaseFlow.BOOKING, GtmPurchaseFlow.CORPORATE].includes(this.purchaseFlow)
        ? GtmEcommerceList.BOOKING_SMP
        : GtmEcommerceList.MMB_VIEW_PNR,
      this.purchaseFlow
    );
  }
}
